import { state, getFaro } from '../state'
import { isPwa, getUtmParams } from '../browser'
import { getAdSessionAttrs } from './ads'
import { touchAcquisition, getAcquisition, consumeAcquisitionRollover, ACQUISITION_UTM_KEYS } from '../stores/acquisition-store'
import { startNewFaroSession } from '../utils/faro-session'
import { derivePathTaxonomy } from '../utils/path-taxonomy'

/** Coerce arbitrary attribute values to strings for Faro meta. Faro only accepts string attrs. */
function stringifyAttrs(input: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null) continue
    if (typeof v === 'string') out[k] = v
    else if (typeof v === 'number' || typeof v === 'boolean') out[k] = String(v)
    // Skip objects/arrays — Faro's MetaAttributes is flat Record<string, string>.
  }
  return out
}

/**
 * Compute the path-taxonomy attribute pair for the current URL. Returns an
 * empty object when window is unavailable (SSR) or the pathname is empty.
 */
function pathTaxonomyAttr(): Record<string, string> {
  const loc = typeof window !== 'undefined' ? window.location : undefined
  if (!loc?.pathname) return {}
  return { path_taxonomy: derivePathTaxonomy(loc.pathname) }
}

/**
 * Page-scoped URL attributes for the CURRENT URL. These are rewritten on each
 * route change so `metadata.page` stays a complete snapshot of the active page.
 * Per-page `utm_*`/`gclid` reflects the current page URL, while session
 * acquisition keeps the visit's first touch.
 */
function currentPageAttrs(): Record<string, string> {
  const loc = typeof window !== 'undefined' ? window.location : undefined
  if (!loc?.href) return {}
  try {
    const url = new URL(loc.href)
    return {
      domain: url.hostname,
      path: url.pathname,
      queryString: url.search,
      ...getUtmParams(url.searchParams),
    }
  } catch {
    return {}
  }
}

/**
 * Reset Faro view + page metadata on route navigation.
 *
 * Why this REPLACES rather than merges: `defu`-merging here was the source of
 * a cross-page attribute leak — semantic attrs set on one route would survive
 * a navigation because defu never clears keys. By calling `setView`/`setPage`
 * with a fresh object, we re-anchor the view to the route name and zero out
 * any stale attrs from the previous page.
 *
 * IMPORTANT: Faro's `MetaView` only exposes `name`. Page-level attrs
 * (route_name, path_taxonomy, …) live in `metas.page.attributes` (and in our
 * in-memory `state.context.view.attributes` mirror).
 */
function resetFaroViewAndPage(name: string, attrs: Record<string, unknown>): void {
  const faro = getFaro()
  if (!faro) return
  const loc = typeof window !== 'undefined' ? window.location : undefined
  const url = loc?.href

  const referrer = typeof document !== 'undefined' ? document.referrer : ''
  faro.api.setView({ name })
  faro.api.setPage({
    ...(url ? { url } : {}),
    attributes: {
      ...currentPageAttrs(),
      route_name: name,
      // Per-page referrer (this document's referrer at navigation time) —
      // distinct from the session's first-touch acquisition referrer.
      ...(referrer ? { referrer } : {}),
      ...stringifyAttrs(attrs),
      ...pathTaxonomyAttr(),
    },
  })
}

/**
 * Refresh `state.context.page` and live session signals (utm_*, is_pwa).
 * Called on every page event so destinations see the current URL/UTMs
 * without needing per-event middlewares.
 */
function refreshContext(): void {
  if (!state.context || typeof window === 'undefined') return

  try {
    const url = new URL(window.location.href)
    state.context.page = {
      url: url.href,
      domain: url.hostname,
      path: url.pathname,
      queryString: url.search,
      referrer: document.referrer,
    }
    if (state.context.session) {
      // Acquisition (referrer/utm/gclid) is first-touch — set once at load and
      // NOT refreshed here, so a later navigation can't overwrite the session's
      // origin. We only keep its session window alive (touch).
      //
      // Exception — `acquisition.resetOnNewUtm`: an SPA navigation arriving with
      // a NEW utm rolls the session over (GA4-style), updating the session's
      // origin utm and starting a fresh Faro session id.
      if (state.config?.acquisition?.resetOnNewUtm) {
        const acquisition = getAcquisition(url.searchParams, { resetOnNewUtm: true })
        if (consumeAcquisitionRollover()) {
          // Replace the session's utm subset with the new campaign (rebuild —
          // no dynamic delete), keeping every other session signal.
          const kept = Object.fromEntries(
            Object.entries(state.context.session).filter(([key]) => !ACQUISITION_UTM_KEYS.includes(key)),
          )
          state.context.session = { ...kept, ...acquisition }
          startNewFaroSession(acquisition)
        }
      }
      else {
        touchAcquisition()
      }
      Object.assign(state.context.session, getAdSessionAttrs(url.searchParams))
      state.context.session.is_pwa = String(isPwa())
    }
  } catch {
    // URL parsing failed — keep existing context
  }
}

/**
 * Record a page view (route-navigation marker).
 *
 * This is the single, unified page API. It is meant to be called
 * **automatically on every route change** (the `@quero/montilla-nuxt` module
 * fires it on `page:finish`); manual calls are allowed but should be rare.
 *
 * It carries only the route identity — there is no `page_type` / commerce
 * payload anymore. Semantic, payload-bearing events (what used to be
 * `page.view('product', …)`) are now plain **custom events**:
 * `montilla.track('<name>', payload)`.
 *
 * - Updates `state.context.view` (in-memory mirror) with the route name.
 * - Resets Faro `view` + `page` metadata to a clean slate anchored to the route.
 * - Dispatches `{ name: 'page_view', attributes: { ...attrs, route_name } }`.
 */
/** Monotonic clock when available (immune to wall-clock changes); ms. */
function nowMs(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
}

// Dwell tracking: stamped on every page() (route enter) and read on exit so
// page_exit reports how long the user stayed on a given route.
let pageEnteredAtMs = 0
let currentRouteName = ''
let pageExitEmitted = false

/**
 * Dispatch a page_exit for the route the user is leaving, carrying its dwell
 * time. Guarded by `pageExitEmitted` so the SPA route-change path and the
 * `pagehide` beacon can't double-fire for the same route.
 */
function emitPageExit(): void {
  if (pageExitEmitted || !currentRouteName) return
  pageExitEmitted = true
  void state.eventBus.dispatch({
    name: 'page_exit',
    domain: state.source,
    attributes: {
      route_name: currentRouteName,
      duration_ms: Math.round(nowMs() - pageEnteredAtMs),
    },
    timestamp: new Date().toISOString(),
  })
}

async function page(name: string, attributes: Record<string, unknown> = {}): Promise<void> {
  refreshContext()
  // SPA navigation: close out the previous route with its dwell time before
  // switching. `pagehide` only fires on a real unload, so without this every
  // intermediate route in a session would go unmeasured.
  if (currentRouteName && currentRouteName !== name) emitPageExit()
  pageEnteredAtMs = nowMs()
  currentRouteName = name
  pageExitEmitted = false
  if (state.context) {
    state.context.view = {
      name,
      attributes: stringifyAttrs(attributes),
    }
  }
  resetFaroViewAndPage(name, attributes)

  await state.eventBus.dispatch({
    name: 'page_view',
    domain: state.source,
    attributes: { ...attributes, route_name: name },
    timestamp: new Date().toISOString(),
  })

  // Bind the page-exit beacon once — page() runs on every navigation, so a
  // per-call listener would stack up across an SPA session. The guard lives on
  // `window` so it survives module re-evaluation (and is resettable in tests).
  //
  // `pagehide` (not `beforeunload`): it fires on real unload AND when the page
  // enters the bfcache, and unlike `beforeunload` it isn't throttled/ignored on
  // mobile — the reliable hook for exit beacons. This covers the final route of
  // the session; intermediate SPA routes are closed out by page() above.
  const w = window as unknown as { __montillaPageExitBound?: boolean }
  if (typeof window !== 'undefined' && !w.__montillaPageExitBound) {
    w.__montillaPageExitBound = true
    window.addEventListener('pagehide', emitPageExit)
  }
}

export { page }
